import {Component, OnInit} from '@angular/core';
import {CheckboxModule} from "primeng/checkbox";
import {SelectModule} from "primeng/select";
import {InputTextModule} from "primeng/inputtext";
import {NgClass, NgIf} from "@angular/common";
import {FormControl, FormGroup, ReactiveFormsModule, ValidationErrors, Validators} from "@angular/forms";
import {Button} from "primeng/button";
import {Ripple} from "primeng/ripple";
import {CustomDialogData} from "../../../core/models/custom-dialog.model";
import {DialogService, DynamicDialog, DynamicDialogRef} from "primeng/dynamicdialog";
import {DeviseService} from "../../../core/services/devise.service";
import {ToastService} from "../../../core/services/toast.service";
import {AccountType, IAccount} from "../../../core/models/account.model";
import {AccountService} from "../../../core/services/account.service";
import {getUnpagedPageable} from "../../../core/models/page.model";
import {InstitutionService} from "../../../core/services/institution.service";
import {getInstitutionTypeByValue, IInstitution, InstitutionType} from "../../../core/models/institution.model";
import {IBank} from "../../../core/models/bank.model";
import {BankService} from "../../../core/services/bank.service";
import {ICurrency} from "../../../core/models/devise.model";

@Component({
    selector: 'app-account-details',
    imports: [
        // Modules
        CheckboxModule, SelectModule, InputTextModule, ReactiveFormsModule,
        // Pipes & Directives
        NgIf, NgClass, Button, Ripple,
    ],
    templateUrl: './account-details.component.html',
    styleUrls: ['./account-details.component.scss']
})
export class AccountDetailsComponent implements OnInit {

  formGroup = new FormGroup({
    type: new FormControl<string>('', {nonNullable: true, validators: [Validators.required]}),
    bank: new FormControl<string>('', {nonNullable: true, validators: [Validators.required]}),
    number: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required, Validators.minLength(5), Validators.maxLength(34)]
    }),
    libelle: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required, Validators.minLength(3), Validators.maxLength(100)]
    }),
    currency: new FormControl<string>('', {nonNullable: true, validators: [Validators.required]}),
    rib: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required, Validators.pattern(/^[0-9]+$/), Validators.minLength(16), Validators.maxLength(24)]
    }),
    ribKey: new FormControl('', {validators: [Validators.pattern(/^[0-9]{1,2}$/)]}),
    iban: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required, Validators.pattern(/^[A-Z]{2}[0-9]{2}[A-Z0-9]+$/), Validators.minLength(15), Validators.maxLength(34)]
    }),
    codeAgency: new FormControl('', {
      nonNullable: true,
      validators: [Validators.required, Validators.minLength(3), Validators.maxLength(10)]
    }),
    institutionId: new FormControl<string>('', { validators: [Validators.required], nonNullable: true}),
  });

  boundData: CustomDialogData | undefined;
  isLoading: boolean = false;
  instance: DynamicDialog | undefined;
  devises: ICurrency[] = [];
  institutions: IInstitution[] = [];
  account: IAccount = {} as IAccount;
  banks: IBank[] = [];
  accountTypes : AccountType[] = Object.values(AccountType);

  constructor(
    private readonly accountService: AccountService,
    private readonly dialogService: DialogService,
    private readonly bankService: BankService,
    private readonly deviseService: DeviseService,
    private readonly toastService: ToastService,
    private readonly ref: DynamicDialogRef,
    private readonly institutionService: InstitutionService
  ) {
    this.instance = this.dialogService.getInstance(this.ref);
  }

  ngOnInit() {
    this.getBanks();
    this.getDevises();
    this.getInstitutions();
    this.boundData = this.instance?.data;
    if (this.boundData?.isView || this.boundData?.isEdit) {
      this.account = this.boundData?.data as IAccount;
      this.formGroup.patchValue({
        ...this.boundData?.data,
        currency: this.account.currency.code,
        bank: this.account.bank.id,
      });
      if (this.boundData?.isView) this.formGroup.disable();
    }
  }

  getBanks() {
    const summary = 'Récupération des banques';
    this.bankService.getAllBanks(getUnpagedPageable()).subscribe({
      next: (page) => this.banks = page.content,
      error: (err) => this.toastService.showToast(summary, err.error, 'error')
    });
  }

  getInstitutions() {
    this.institutionService.getInstitutionByType(
      [getInstitutionTypeByValue(InstitutionType.BANK)!],
      getUnpagedPageable()
    ).subscribe({
      next: (page) => this.institutions = page.content,
      error: (err) => {
        const summary = 'Récupération des institutions';
        this.toastService.showToast(summary, err.error, 'error');
      }
    });
  }

  getDevises() {
    this.deviseService.getAllDevises(getUnpagedPageable()).subscribe({
      next: (page) => this.devises = page.content,
      error: (err) => this.toastService.showToast('Récupération des devises', err.error, 'error')
    });
  }

  /**
   * Récupère le message d'erreur pour un champ donné
   */
  getFieldError(fieldName: string): string {
    const control = this.formGroup.get(fieldName);
    if (!control || !control.errors || !control.touched) {
      return '';
    }

    const errors = control.errors;
    if (errors['required']) return 'Ce champ est requis';
    if (errors['minlength']) return `Minimum ${errors['minlength'].requiredLength} caractères`;
    if (errors['maxlength']) return `Maximum ${errors['maxlength'].requiredLength} caractères`;
    if (errors['pattern']) {
      if (fieldName === 'rib') return 'Le RIB doit contenir uniquement des chiffres';
      if (fieldName === 'ribKey') return 'La clé RIB doit contenir 1 ou 2 chiffres';
      if (fieldName === 'iban') return 'Format IBAN invalide (ex: FR7630001007941234567890185)';
    }

    return 'Valeur invalide';
  }

  /**
   * Vérifie si un champ a une erreur et a été touché
   */
  hasFieldError(fieldName: string): boolean {
    const control = this.formGroup.get(fieldName);
    return !!(control && control.errors && control.touched);
  }

  /**
   * Marque tous les champs comme touchés pour afficher les erreurs
   */
  private markAllFieldsAsTouched(): void {
    Object.keys(this.formGroup.controls).forEach(key => {
      const control = this.formGroup.get(key);
      control?.markAsTouched();
    });
  }

  /**
   * Gère les erreurs HTTP et affiche un message approprié
   */
  private handleHttpError(error: any, operation: string): void {
    let errorMessage = 'Une erreur est survenue. Veuillez réessayer.';

    if (error.error) {
      if (typeof error.error === 'string') {
        errorMessage = error.error;
      } else if (error.error.message) {
        errorMessage = error.error.message;
      } else if (error.error.error) {
        errorMessage = error.error.error;
      }
    } else if (error.message) {
      errorMessage = error.message;
    }

    // Messages d'erreur spécifiques selon le code HTTP
    if (error.status === 400) {
      errorMessage = 'Données invalides. ' + errorMessage;
    } else if (error.status === 401) {
      errorMessage = 'Non autorisé. Veuillez vous reconnecter.';
    } else if (error.status === 403) {
      errorMessage = 'Accès refusé. Vous n\'avez pas les permissions nécessaires.';
    } else if (error.status === 404) {
      errorMessage = 'Compte non trouvé.';
    } else if (error.status === 409) {
      errorMessage = 'Conflit. Un compte avec ce numéro existe déjà.';
    } else if (error.status === 500) {
      errorMessage = 'Erreur serveur. Veuillez contacter l\'administrateur.';
    } else if (error.status === 0) {
      errorMessage = 'Impossible de contacter le serveur. Vérifiez votre connexion.';
    }

    this.toastService.showToast(
      `Erreur lors de la ${operation}`,
      errorMessage,
      'error'
    );
  }

  create() {
    // Validation du formulaire avant soumission
    if (this.formGroup.invalid) {
      this.markAllFieldsAsTouched();
      this.toastService.showToast(
        'Formulaire invalide',
        'Veuillez corriger les erreurs avant de soumettre le formulaire',
        'warn'
      );
      return;
    }

    this.isLoading = true;
    const accountDto = this.parseAccount();
    if (accountDto === undefined) return;
    this.accountService.createAccount(accountDto).subscribe({
      next: (account) => {
        this.toastService.showToast('Compte créé', `Le compte ${account.libelle} a été créé avec succès.`);
        this.isLoading = false;
        this.ref.close();
      },
      error: (error) => {
        this.isLoading = false;
        this.handleHttpError(error, 'création');
      }
    });
  }

  update() {
    // Validation du formulaire avant soumission
    if (this.formGroup.invalid) {
      this.markAllFieldsAsTouched();
      this.toastService.showToast(
        'Formulaire invalide',
        'Veuillez corriger les erreurs avant de soumettre le formulaire',
        'warn'
      );
      return;
    }

    this.isLoading = true;
    const accountDto = this.parseAccount();
    if (accountDto === undefined) return;
    this.accountService.updateAccount(this.boundData?.data.id, accountDto).subscribe({
      next: (account) => {
        this.toastService.showToast('Compte modifié', `Le compte ${account.libelle} a été modifié avec succès.`);
        this.isLoading = false;
        this.ref.close();
      },
      error: (error) => {
        this.isLoading = false;
        this.handleHttpError(error, 'modification');
      }
    });
  }

  private parseAccount() {
    this.isLoading = true;
    const currency = this.devises.find(devise => devise.code === this.formGroup.value.currency);
    const bank = this.banks.find(bank => bank.id === this.formGroup.value.bank);
    if (currency === undefined) {
      this.toastService.showToast('Création de compte', 'La devise est obligatoire', 'error');
      this.isLoading = false;
      return;
    }
    if (bank === undefined) {
      this.toastService.showToast('Création de compte', 'La banque est obligatoire', 'error');
      this.isLoading = false;
      return;
    }
    const account = this.formGroup.value as unknown as IAccount;
    account.bank = bank;
    account.currency = currency;
    return account;
  }

}
